import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import Typography from '@src/new-components/Typography';
import { formatRewardCode } from '@src/services/topPaidEvent';
import { COLOR } from '@src/styles/color';

// Past 3 rows the grid scrolls.
const SCROLLABLE_CODE_COUNT = 6;
const FADE_SIZE = '28px';

// Half-circle cut-outs on both short edges so each code reads as a ticket stub.
const TICKET_NOTCH_MASK = [
  'radial-gradient(circle at 0 50%, transparent 8px, #000 8.5px) left / 51% 100% no-repeat',
  'radial-gradient(circle at 100% 50%, transparent 8px, #000 8.5px) right / 51% 100% no-repeat',
].join(', ');

const StyledRewardCodeGrid = styled('div', {
  shouldForwardProp: (prop) =>
    !['single', 'fadeTop', 'fadeBottom'].includes(prop),
})(({ single, fadeTop, fadeBottom }) => {
  // Only the edges that still hide codes fade out.
  const top = fadeTop ? FADE_SIZE : '0px';
  const bottom = fadeBottom ? FADE_SIZE : '0px';
  const fadeMask = `linear-gradient(to bottom, transparent 0, #000 ${top}, #000 calc(100% - ${bottom}), transparent 100%)`;

  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    width: '100%',

    '.received-text': { color: COLOR.darkGray, textAlign: 'center' },
    '.code-grid': {
      display: 'grid',
      gridTemplateColumns: single ? 'minmax(0, 226px)' : 'repeat(2, 1fr)',
      justifyContent: 'center',
      gap: 12,
      width: '100%',
      maxHeight: 200,
      overflowY: 'auto',
      WebkitMaskImage: fadeMask,
      maskImage: fadeMask,
    },
    '.code-item': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 64,
      borderRadius: 12,
      border: `1px solid ${COLOR.white60}`,
      background: `linear-gradient(135deg, ${COLOR.amber100} 0%, ${COLOR.amber300} 45%, ${COLOR.amber500} 100%)`,
      boxShadow: `inset 0 1px 0 ${COLOR.white70}`,
      color: COLOR.amber900,
      WebkitMask: TICKET_NOTCH_MASK,
      mask: TICKET_NOTCH_MASK,
    },
    '.code-text': {
      letterSpacing: '1.5px',
      fontVariantNumeric: 'tabular-nums',
      textShadow: `0 1px 0 ${COLOR.white48}`,
    },
  };
});

// Codes the user just earned, shown inside the purchase-success popups.
const RewardCodeGrid = ({ codes }) => {
  const { t } = useTranslation();
  const gridRef = useRef(null);
  const [fades, setFades] = useState({ top: false, bottom: false });
  const scrollable = codes.length > SCROLLABLE_CODE_COUNT;

  const updateFades = () => {
    const grid = gridRef.current;
    if (!grid || !scrollable) {
      setFades({ top: false, bottom: false });
      return;
    }
    setFades({
      top: grid.scrollTop > 1,
      bottom: grid.scrollTop + grid.clientHeight < grid.scrollHeight - 1,
    });
  };

  useLayoutEffect(updateFades, [codes.length]);

  return (
    <StyledRewardCodeGrid
      single={codes.length === 1}
      fadeTop={fades.top}
      fadeBottom={fades.bottom}
    >
      <Typography variant="body-16-regular" className="received-text">
        {t('topPaidEventReceivedCodes', { count: codes.length })}
      </Typography>
      <div className="code-grid" ref={gridRef} onScroll={updateFades}>
        {codes.map((code) => (
          <div className="code-item" key={code}>
            <Typography variant="body-24-bold" className="code-text">
              {formatRewardCode(code)}
            </Typography>
          </div>
        ))}
      </div>
    </StyledRewardCodeGrid>
  );
};

export default RewardCodeGrid;
